import React from "react";

import { TailwindSelect } from "../../Tailwind";
import snakeToPascal from "../../../tools";
import { initRelation } from "./Initializer";
import apiService from "../../../services/ApiService";
import { useUpdateFieldValue } from "../../../shared/hooks/mutations/useUpdateFieldValue";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import FieldError from "../FieldError";
import { useEditorStore } from "../../../shared/stores/useEditorStore";

export default function TimingReferenceField({ name, field }) {
    const [mounted, setMounted] = React.useState(false);
    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave } = useSaveIndicator();
    const [timingChoicesItems, setTimingChoicesItems] = React.useState([]);
    const [optionsLoading, setOptionsLoading] = React.useState(true);
    const updateField = useUpdateFieldValue();
    const isReadOnly = !useEditorStore(s => s.userRights.right_to_write);
    const scenarioId = useEditorStore(s => s.scenarioId);

    const reloadLocalState = () => {
        setMounted(!mounted);
    }

    React.useEffect(() => {
        // Global library snapshot: its ids belong to another database.
        if (field.snapshot) {
            setOptionsLoading(false);
            return;
        }
        setOptionsLoading(true);
        apiService.getTimingOptions(field.id).then(async (response) => {
            if (response) {
                const data = await response.json();
                setTimingChoicesItems(data);

                // Set timing choice by id if needed
                if (field.currentValue) {
                    data.forEach((item: any) => {
                        if (item.id == field.currentValue.value) {
                            if (field.currentValue.timingChoice == 'null') {
                                field.currentValue.timingChoice = item;
                                reloadLocalState();
                            }
                        }
                    });
                }
            }
            setOptionsLoading(false);
        });
    }, [field.id]);

    const handleChange = (newValueId: string) => {
        field.compute_error = null;
        reloadLocalState();
        const apiValue = (newValueId && newValueId !== 'null') ? parseInt(newValueId) : null;

        triggerSave();
        updateField.mutate(
            { id: field.id, value: apiValue, scenarioId: scenarioId ?? 0 },
            {
                onSuccess: () => {
                    if (field.currentValue) {
                        field.currentValue.value = apiValue ? apiValue.toString() : "null";

                        // Update timingChoice to the selected timing object
                        const selectedTiming = timingChoicesItems.find((item: any) => item.id.toString() === newValueId);
                        field.currentValue.timingChoice = selectedTiming || 'null';
                    }
                    resolveSave();
                    reloadLocalState();
                },
            }
        );
    }

    const options = timingChoicesItems.map((timing: any) => ({
        value: timing.id.toString(),
        label: `${timing.parent_name} | ${snakeToPascal(timing.field_name)}`,
    }));

    // Gérer le cas où currentValue est undefined (nouveau model data)
    const safeCurrentValue = field.currentValue?.value ?? field.value?.value ?? 'null';
    const currentValue = safeCurrentValue !== 'null' ? safeCurrentValue : '';

    return (
        <div className="mb-1">
            <div className="flex flex-row items-center">
                <TailwindSelect
                    value={currentValue}
                    onChange={handleChange}
                    options={options}
                    placeholder={snakeToPascal(name)}
                    disabled={isReadOnly || optionsLoading}
                    className="w-[200px]"
                />
                <SaveIndicator status={saveStatus} className="ml-2" />
            </div>
            <FieldError field={field} />
        </div>
    )
}
